import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import pinyin from 'pinyin';
import ProductGrid from '../BodyComponent/ProductGrid';
import Pagination from '../BodyComponent/Pagination';
import ExplanationProduct from '../BodyComponent/ExplanationProduct';
import CustomModal from '../BodyComponent/CustomModal';
import { getApiUrl } from '../config/api';
import ProductGridSkeleton from '../BodyComponent/ProductGridSkeleton';
import { useQuery } from '@tanstack/react-query';

function ProductSeriesPage({ seriesOptions }) {
    // 1. 取得 URL 參數與導航
    const { series } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // 2. 解析目前系列
    const currentSeries = Array.isArray(seriesOptions)
        ? seriesOptions.find(option => {
            const optionPinyin = pinyin(option, { style: pinyin.STYLE_NORMAL }).join('').replace(/\//g, '_');
            return optionPinyin === series;
        })
        : undefined;

    // 3. 若找不到系列則導回 stock
    useEffect(() => {
        // 只有在 seriesOptions 已經載入完成（不是空陣列）時才進行導航
        if (Array.isArray(seriesOptions) && seriesOptions.length > 0 && !currentSeries) {
            navigate('/stock');
        }
    }, [currentSeries, navigate, seriesOptions]);

    // 狀態管理
    const [itemsPerPage, setItemsPerPage] = useState(25);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState('');
    const [selectedBarcode, setSelectedBarcode] = useState('');

    // 取得目前頁碼
    const page = parseInt(searchParams.get('page')) || 1;

    // 響應式每頁顯示數量
    const updateItemsPerPage = useCallback(() => {
        const width = window.innerWidth;
        setItemsPerPage(width <= 768 ? 10 : 25);
    }, []);

    useEffect(() => {
        updateItemsPerPage();
        window.addEventListener('resize', updateItemsPerPage);
        const shouldReset = sessionStorage.getItem('resetSeriesPage') === 'true';
        if (shouldReset) {
            setSearchParams({ page: '1' }, { replace: true });
            sessionStorage.removeItem('resetSeriesPage');
        }
        return () => window.removeEventListener('resize', updateItemsPerPage);
    }, [updateItemsPerPage, setSearchParams]);

    // 用 useQuery 快取 API 請求
    const { data, isLoading, error } = useQuery({
        queryKey: ['seriesProducts', currentSeries, page, itemsPerPage],
        queryFn: async () => {
            if (!currentSeries) return { products: [], totalPages: 0 };
            const apiUrl = getApiUrl('/orders/get_series_product_content');
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    series_name: currentSeries,
                    page: page,
                    limit: itemsPerPage
                })
            });
            const data = await response.json();
            return {
                products: data.data.map(item => ({
                    id: item.id,
                    barcode: item.條碼,
                    product_name: item.品名,
                    remaining_stock: item.剩餘庫存,
                    visible_quantity: item.現貨系統可販售數,
                    system_price: item.現貨系統價格,
                    system_date: item.現貨系統活動,
                    producer: item.出品商,
                    series: item.系列,
                    product_type: item.類型,
                    role1: item.角色1,
                    role2: item.角色2,
                    role3: item.角色3,
                    features: item.其他,
                    specification: item.規格,
                    imageUrl: `https://cdn.jsdelivr.net/gh/WaynezProg/product_picture/all/${item.條碼}.webp`,
                    productUrl: `https://chiikawamarket.jp/products/${item.條碼}`
                })),
                totalPages: data.total_pages === 0 ? 1 : data.total_pages || 0
            };
        },
        enabled: !!currentSeries, // 沒有系列時不發 request
        keepPreviousData: true,
        staleTime: 30 * 60 * 1000
    });
    const totalPages = data?.totalPages || 0;

    // page 合法性檢查
    useEffect(() => {
        if (!searchParams.get('page') || isNaN(page) || page < 1 || (totalPages && page > totalPages)) {
            setSearchParams({ page: '1' }, { replace: true });
        }
    }, [searchParams, totalPages, setSearchParams, page]);

    // 滾動到最上方
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [page, currentSeries]);

    // 分頁切換
    const handlePageChange = (newPage) => {
        setSearchParams({ page: newPage.toString() });
    };

    // 使用 CustomModal 顯示圖片與加入購物車
    const openModal = (imageUrl, barcode) => {
        const img = new window.Image();
        img.src = imageUrl;
        img.onload = () => {
            setSelectedImage(imageUrl);
            setSelectedBarcode(barcode);
            setIsModalOpen(true);
        };
        img.onerror = () => {
            setSelectedImage('/404.jpg');
            setSelectedBarcode(barcode);
            setIsModalOpen(true);
        };
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedImage('');
        setSelectedBarcode('');
    };

    const handleOpenModal = (imageUrl, barcode) => {
        openModal(imageUrl, barcode);
    };

    // 取用快取資料
    const productData = data?.products || [];

    // 13. UI
    return (
        <div>
            <ExplanationProduct />
            <h1>{currentSeries || '未找到'}</h1>
            {isLoading ? (
                <ProductGridSkeleton count={itemsPerPage} />
            ) : error ? (
                <div>資料載入失敗，請稍後再試。</div>
            ) : (
                <div>
                    <ProductGrid productData={productData} openModal={handleOpenModal} />
                    <Pagination 
                        currentPage={page} 
                        totalPages={totalPages} 
                        onPageChange={handlePageChange} 
                    />
                </div>
            )}
            <CustomModal
                isOpen={isModalOpen}
                onClose={closeModal}
                imageSrc={selectedImage}
                barcode={selectedBarcode}
            />
        </div>
    );
}

export default ProductSeriesPage; 